/**
 * ATT-3384 admin half — boot-time fetch of the override target's branding.
 *
 * When a staging tenant-override is active (`?__tenant=` / cookie, gated by
 * `NEXT_PUBLIC_ALLOW_TENANT_OVERRIDE`), fetch the TARGET host's branding via
 * `tenantBrandingByHost(host, portal="admin")` and populate the override-branding
 * store. Subscribes to the store so `<App />` re-renders once the fetch resolves —
 * first paint shows real staging branding, then hydrates to the target (same
 * first-paint model as tenant discovery; render is never blocked on the fetch).
 *
 * Inert in production: `getTenantOverrideHost()` returns null when the gate is
 * off, so the query is never issued there.
 */

import { useEffect } from "react";
import { getTenantOverrideHost, normalizeHost } from "../../config/tenants";
import { fetchTenantBrandingByHost } from "./fetchBranding";
import {
  hasOverrideBrandingEntry,
  setOverrideBranding,
  useTenantOverrideBrandingStore,
} from "./store";

const OVERRIDE_PORTAL = "admin" as const;

export function useTenantOverrideBranding(): void {
  // Subscribe so the host component re-renders when the target's branding lands.
  useTenantOverrideBrandingStore((s) => s.byHost);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const target = getTenantOverrideHost(); // null when the gate is off or no override
    if (!target) return undefined;
    const normalized = normalizeHost(target);
    if (hasOverrideBrandingEntry(normalized)) return undefined; // already fetched (or known-unknown)

    let cancelled = false;
    fetchTenantBrandingByHost(normalized, OVERRIDE_PORTAL).then((branding) => {
      if (cancelled) return;
      // Store the result either way — a null (unknown host) marks it resolved so
      // it isn't re-fetched, and resolution stays on the real staging tenant.
      setOverrideBranding(normalized, branding);
    });

    return () => {
      cancelled = true;
    };
  }, []);
}
